/**
 * TrackedLink - Anchor link with built-in analytics tracking
 *
 * @module components/Shared/TrackedLink
 * @description A link component that tracks outbound clicks and contact interactions
 *
 * @example
 * ```tsx
 * // Phone link
 * <TrackedLink href="tel:+1234567890" type="phone">
 *   Call Us
 * </TrackedLink>
 *
 * // Email link
 * <TrackedLink href="mailto:info@example.com" type="email">
 *   Email Us
 * </TrackedLink>
 *
 * // External link
 * <TrackedLink href="https://twitter.com/example" type="social" platform="Twitter">
 *   Follow Us
 * </TrackedLink>
 * ```
 */

import React from "react";
import {
  trackOutboundLink,
  trackPhoneClick,
  trackEmailClick,
  trackSocialClick,
} from "@/lib/analytics";

type LinkType = "external" | "phone" | "email" | "social" | "download";

interface TrackedLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'type'> {
  /** Link URL */
  href: string;
  /** Link content */
  children: React.ReactNode;
  /** Type of link for appropriate tracking */
  type?: LinkType;
  /** Social platform name (required when type="social") */
  platform?: string;
  /** File name for downloads (required when type="download") */
  fileName?: string;
  /** Open in new tab */
  newTab?: boolean;
}

const TrackedLink: React.FC<TrackedLinkProps> = ({
  href,
  children,
  type = "external",
  className = "",
  platform,
  fileName,
  newTab = true,
  ...rest
}) => {
  const handleClick = () => {
    switch (type) {
      case "phone": {
        const phoneNumber = href.replace("tel:", "");
        trackPhoneClick(phoneNumber);
        break;
      }
      case "email": {
        const email = href.replace("mailto:", "").split("?")[0];
        trackEmailClick(email);
        break;
      }
      case "social": {
        trackSocialClick(platform || "Unknown", href);
        break;
      }
      case "download": {
        // Download tracking handled separately
        break;
      }
      default:
        trackOutboundLink(href);
    }
  };

  const linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
    href,
    onClick: handleClick,
    className,
  };

  if (newTab && type !== "phone" && type !== "email") {
    linkProps.target = "_blank";
    linkProps.rel = "noopener noreferrer";
  }

  if (type === "download" && fileName) {
    linkProps.download = fileName;
  }

  return <a {...linkProps} {...rest}>{children}</a>;
};

export default TrackedLink;
